/**
 * MANDO · by Leapifyes
 * Sistema de Validaciones Profesionales para España
 * 
 * Este módulo implementa validaciones estrictas pero UX-friendly
 * para asegurar datos reales, coherentes y utilizables.
 */

// ==================== CONSTANTES ====================

// Valores genéricos bloqueados
const BLOCKED_GENERIC_VALUES = [
  'prueba', 'test', 'cliente', 'xxx', 'aaa', 'bbb', 'ccc',
  'asdf', 'qwerty', 'demo', 'ejemplo', 'sample', 'fake',
  'none', 'n/a', 'na', 'null', 'undefined', 'nada', 'no',
  'sin', 'pendiente', 'revisar', 'completar'
];

// Secuencias de teléfono bloqueadas
const BLOCKED_PHONE_SEQUENCES = [
  '000000000', '111111111', '222222222', '333333333',
  '444444444', '555555555', '666666666', '777777777',
  '888888888', '999999999', '123456789', '987654321',
  '123123123', '456456456', '789789789', '121212121'
];

// Extensiones de email permitidas
const ALLOWED_EMAIL_EXTENSIONS = ['.es', '.com', '.net', '.org', '.eu', '.info', '.biz', '.co'];

// Países de la UE
export const EU_COUNTRIES = [
  { code: 'ES', name: 'España', phoneCode: '+34' },
  { code: 'PT', name: 'Portugal', phoneCode: '+351' },
  { code: 'FR', name: 'Francia', phoneCode: '+33' },
  { code: 'DE', name: 'Alemania', phoneCode: '+49' },
  { code: 'IT', name: 'Italia', phoneCode: '+39' },
  { code: 'BE', name: 'Bélgica', phoneCode: '+32' },
  { code: 'NL', name: 'Países Bajos', phoneCode: '+31' },
  { code: 'AT', name: 'Austria', phoneCode: '+43' },
  { code: 'PL', name: 'Polonia', phoneCode: '+48' },
  { code: 'IE', name: 'Irlanda', phoneCode: '+353' },
  { code: 'GR', name: 'Grecia', phoneCode: '+30' },
  { code: 'SE', name: 'Suecia', phoneCode: '+46' },
  { code: 'DK', name: 'Dinamarca', phoneCode: '+45' },
  { code: 'FI', name: 'Finlandia', phoneCode: '+358' },
  { code: 'CZ', name: 'República Checa', phoneCode: '+420' },
  { code: 'RO', name: 'Rumanía', phoneCode: '+40' },
  { code: 'HU', name: 'Hungría', phoneCode: '+36' },
  { code: 'BG', name: 'Bulgaria', phoneCode: '+359' },
  { code: 'HR', name: 'Croacia', phoneCode: '+385' },
  { code: 'SK', name: 'Eslovaquia', phoneCode: '+421' },
  { code: 'SI', name: 'Eslovenia', phoneCode: '+386' },
  { code: 'LT', name: 'Lituania', phoneCode: '+370' },
  { code: 'LV', name: 'Letonia', phoneCode: '+371' },
  { code: 'EE', name: 'Estonia', phoneCode: '+372' },
  { code: 'CY', name: 'Chipre', phoneCode: '+357' },
  { code: 'MT', name: 'Malta', phoneCode: '+356' },
  { code: 'LU', name: 'Luxemburgo', phoneCode: '+352' },
  { code: 'OTHER', name: 'Otro país', phoneCode: '' },
];

// ==================== VALIDADORES ====================

/**
 * Valida un teléfono español
 * - 9 dígitos exactos
 * - Móviles: empiezan por 6 o 7
 * - Fijos: empiezan por 8 o 9
 */
export const validatePhone = (phone, options = {}) => {
  const { required = true, preferMobile = true } = options;
  
  // Limpiar el teléfono (quitar espacios, guiones, prefijo)
  let cleanPhone = phone?.toString().trim() || '';
  cleanPhone = cleanPhone.replace(/[\s\-\.\(\)]/g, '');
  cleanPhone = cleanPhone.replace(/^\+34/, '');
  
  // Si está vacío y no es requerido, OK
  if (!cleanPhone && !required) {
    return { valid: true, phone: '' };
  }
  
  // Si está vacío y es requerido
  if (!cleanPhone) {
    return { 
      valid: false, 
      error: 'El teléfono es obligatorio',
      phone: ''
    };
  }
  
  // Solo dígitos
  if (!/^\d+$/.test(cleanPhone)) {
    return {
      valid: false,
      error: 'El teléfono solo puede contener números',
      phone: cleanPhone
    };
  }
  
  // Longitud exacta 9 dígitos
  if (cleanPhone.length !== 9) {
    return {
      valid: false,
      error: 'El teléfono debe tener 9 dígitos',
      phone: cleanPhone
    };
  }
  
  // Verificar secuencias bloqueadas
  if (BLOCKED_PHONE_SEQUENCES.includes(cleanPhone)) {
    return {
      valid: false,
      error: 'Introduce un teléfono válido, no secuencias repetidas',
      phone: cleanPhone
    };
  }
  
  // Verificar prefijo válido (6, 7 para móvil; 8, 9 para fijo)
  const firstDigit = cleanPhone[0];
  const validPrefixes = ['6', '7', '8', '9'];
  const mobilePrefixes = ['6', '7'];
  
  if (!validPrefixes.includes(firstDigit)) {
    return {
      valid: false,
      error: 'El teléfono debe empezar por 6, 7, 8 o 9',
      phone: cleanPhone
    };
  }
  
  // Advertencia si no es móvil (pero válido)
  const isMobile = mobilePrefixes.includes(firstDigit);
  
  return {
    valid: true,
    phone: cleanPhone,
    formatted: `${cleanPhone.slice(0,3)} ${cleanPhone.slice(3,6)} ${cleanPhone.slice(6)}`,
    isMobile,
    warning: preferMobile && !isMobile ? 'Recomendamos usar un número móvil para mejor contacto' : null
  };
};

/**
 * Valida un email
 * - Formato correcto
 * - Extensiones permitidas
 * - Sin caracteres inválidos
 */
export const validateEmail = (email, options = {}) => {
  const { required = true } = options;
  
  const cleanEmail = email?.toString().trim().toLowerCase() || '';
  
  // Si está vacío y no es requerido, OK
  if (!cleanEmail && !required) {
    return { valid: true, email: '' };
  }
  
  // Si está vacío y es requerido
  if (!cleanEmail) {
    return {
      valid: false,
      error: 'El email es obligatorio',
      email: ''
    };
  }
  
  // Verificar formato básico
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(cleanEmail)) {
    return {
      valid: false,
      error: 'Introduce un email válido (ej: nombre@empresa.es)',
      email: cleanEmail
    };
  }
  
  // Verificar que no tenga múltiples @
  if ((cleanEmail.match(/@/g) || []).length > 1) {
    return {
      valid: false,
      error: 'El email no puede contener múltiples @',
      email: cleanEmail
    };
  }
  
  // Verificar extensión
  const hasValidExtension = ALLOWED_EMAIL_EXTENSIONS.some(ext => cleanEmail.endsWith(ext));
  if (!hasValidExtension) {
    return {
      valid: false,
      error: 'Usa una extensión válida (.es, .com, .net, .org, .eu)',
      email: cleanEmail
    };
  }
  
  // Verificar que el dominio tenga al menos un punto después del @
  const domain = cleanEmail.split('@')[1];
  if (!domain || !domain.includes('.')) {
    return {
      valid: false,
      error: 'El dominio del email no es válido',
      email: cleanEmail
    };
  }
  
  return {
    valid: true,
    email: cleanEmail
  };
};

/**
 * Valida una dirección española
 * - No puede ser genérica
 * - Debe tener estructura real (calle + número + ciudad)
 */
export const validateAddress = (address, options = {}) => {
  const { required = true } = options;
  
  const cleanAddress = address?.toString().trim() || '';
  
  // Si está vacío y no es requerido, OK
  if (!cleanAddress && !required) {
    return { valid: true, address: '' };
  }
  
  // Si está vacío y es requerido
  if (!cleanAddress) {
    return {
      valid: false,
      error: 'La dirección es obligatoria',
      address: ''
    };
  }
  
  // Verificar longitud mínima
  if (cleanAddress.length < 10) {
    return {
      valid: false,
      error: 'La dirección es demasiado corta. Incluye calle, número y ciudad',
      address: cleanAddress
    };
  }
  
  // Verificar que no sea una sola palabra
  const words = cleanAddress.split(/[\s,]+/).filter(w => w.length > 0);
  if (words.length < 2) {
    return {
      valid: false,
      error: 'Introduce una dirección completa (calle, número, ciudad)',
      address: cleanAddress
    };
  }
  
  // Verificar valores genéricos
  const lowerAddress = cleanAddress.toLowerCase();
  if (BLOCKED_GENERIC_VALUES.some(blocked => lowerAddress === blocked || lowerAddress.startsWith(blocked + ' '))) {
    return {
      valid: false,
      error: 'Introduce una dirección real',
      address: cleanAddress
    };
  }
  
  // Verificar que contenga al menos un número (para el número de calle)
  const hasNumber = /\d/.test(cleanAddress);
  if (!hasNumber) {
    return {
      valid: false,
      error: 'Incluye el número de la calle en la dirección',
      address: cleanAddress,
      warning: true // Es una advertencia, no un bloqueo total
    };
  }
  
  return {
    valid: true,
    address: cleanAddress
  };
};

/**
 * Valida un DNI español
 * - 8 números + 1 letra
 * - Valida la letra de control
 */
export const validateDNI = (dni) => {
  const cleanDNI = dni?.toString().trim().toUpperCase() || '';
  
  if (!cleanDNI) {
    return { valid: false, error: 'DNI requerido' };
  }
  
  // Formato: 8 números + 1 letra
  const dniRegex = /^(\d{8})([A-Z])$/;
  const match = cleanDNI.match(dniRegex);
  
  if (!match) {
    return {
      valid: false,
      error: 'El DNI debe tener 8 números y una letra (ej: 12345678Z)',
      dni: cleanDNI
    };
  }
  
  // Validar letra de control
  const numbers = parseInt(match[1], 10);
  const letter = match[2];
  const validLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const expectedLetter = validLetters[numbers % 23];
  
  if (letter !== expectedLetter) {
    return {
      valid: false,
      error: 'La letra del DNI no es correcta',
      dni: cleanDNI
    };
  }
  
  return {
    valid: true,
    dni: cleanDNI,
    formatted: `${match[1]}-${match[2]}`
  };
};

/**
 * Valida un NIE español
 * - X/Y/Z + 7 números + letra
 * - Valida la letra de control
 */
export const validateNIE = (nie) => {
  const cleanNIE = nie?.toString().trim().toUpperCase() || '';
  
  if (!cleanNIE) {
    return { valid: false, error: 'NIE requerido' };
  }
  
  // Formato: X/Y/Z + 7 números + letra
  const nieRegex = /^([XYZ])(\d{7})([A-Z])$/;
  const match = cleanNIE.match(nieRegex);
  
  if (!match) {
    return {
      valid: false,
      error: 'El NIE debe ser X/Y/Z seguido de 7 números y una letra (ej: X1234567L)',
      nie: cleanNIE
    };
  }
  
  // Convertir primera letra a número para el cálculo
  const firstLetter = match[1];
  const numbers = match[2];
  const letter = match[3];
  
  let nieNumber;
  if (firstLetter === 'X') nieNumber = '0' + numbers;
  else if (firstLetter === 'Y') nieNumber = '1' + numbers;
  else if (firstLetter === 'Z') nieNumber = '2' + numbers;
  
  // Validar letra de control
  const validLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const expectedLetter = validLetters[parseInt(nieNumber, 10) % 23];
  
  if (letter !== expectedLetter) {
    return {
      valid: false,
      error: 'La letra del NIE no es correcta',
      nie: cleanNIE
    };
  }
  
  return {
    valid: true,
    nie: cleanNIE,
    formatted: `${match[1]}-${match[2]}-${match[3]}`
  };
};

/**
 * Valida un CIF español
 * - 1 letra + 7 números + 1 carácter de control
 */
export const validateCIF = (cif) => {
  const cleanCIF = cif?.toString().trim().toUpperCase() || '';
  
  if (!cleanCIF) {
    return { valid: false, error: 'CIF requerido' };
  }
  
  // Formato: Letra + 7 números + letra/número
  const cifRegex = /^([ABCDEFGHJKLMNPQRSUVW])(\d{7})([A-J0-9])$/;
  const match = cleanCIF.match(cifRegex);
  
  if (!match) {
    return {
      valid: false,
      error: 'El CIF debe tener formato válido (ej: B12345678)',
      cif: cleanCIF
    };
  }
  
  // Cálculo del dígito de control
  const letter = match[1];
  const numbers = match[2];
  const control = match[3];
  
  let sumA = 0;
  let sumB = 0;
  
  for (let i = 0; i < 7; i++) {
    const digit = parseInt(numbers[i], 10);
    if (i % 2 === 0) {
      // Posiciones impares (0, 2, 4, 6): multiplicar por 2 y sumar dígitos
      const doubled = digit * 2;
      sumB += doubled >= 10 ? Math.floor(doubled / 10) + (doubled % 10) : doubled;
    } else {
      // Posiciones pares (1, 3, 5): sumar directamente
      sumA += digit;
    }
  }
  
  const total = sumA + sumB;
  const lastDigit = total % 10;
  const expectedDigit = lastDigit === 0 ? 0 : 10 - lastDigit;
  const expectedLetter = 'JABCDEFGHI'[expectedDigit];
  
  // Algunas letras iniciales requieren letra de control, otras número
  const requiresLetter = ['K', 'P', 'Q', 'S'].includes(letter);
  const requiresNumber = ['A', 'B', 'E', 'H'].includes(letter);
  
  let isValid = false;
  if (requiresLetter) {
    isValid = control === expectedLetter;
  } else if (requiresNumber) {
    isValid = control === expectedDigit.toString();
  } else {
    isValid = control === expectedLetter || control === expectedDigit.toString();
  }
  
  if (!isValid) {
    return {
      valid: false,
      error: 'El dígito de control del CIF no es correcto',
      cif: cleanCIF
    };
  }
  
  return {
    valid: true,
    cif: cleanCIF
  };
};

/**
 * Valida cualquier documento español según su tipo
 */
export const validateDocument = (document, type, options = {}) => {
  const { required = false } = options;
  
  const cleanDoc = document?.toString().trim() || '';
  
  // Si está vacío y no es requerido, OK
  if (!cleanDoc && !required) {
    return { valid: true, document: '' };
  }
  
  // Si está vacío y es requerido
  if (!cleanDoc && required) {
    return {
      valid: false,
      error: 'El documento es obligatorio',
      document: ''
    };
  }
  
  // Validar según tipo
  switch (type) {
    case 'DNI':
      return validateDNI(cleanDoc);
    case 'NIE':
    case 'TIE':
      return validateNIE(cleanDoc);
    case 'CIF':
    case 'NIF':
      return validateCIF(cleanDoc);
    case 'OTRO':
      // Campo libre pero con mínimo 5 caracteres
      if (cleanDoc.length < 5) {
        return {
          valid: false,
          error: 'El documento debe tener al menos 5 caracteres',
          document: cleanDoc
        };
      }
      // No permitir valores genéricos
      if (BLOCKED_GENERIC_VALUES.includes(cleanDoc.toLowerCase())) {
        return {
          valid: false,
          error: 'Introduce un documento válido',
          document: cleanDoc
        };
      }
      return { valid: true, document: cleanDoc };
    default:
      // Intentar detectar automáticamente
      if (/^\d{8}[A-Z]$/i.test(cleanDoc)) {
        return validateDNI(cleanDoc);
      } else if (/^[XYZ]\d{7}[A-Z]$/i.test(cleanDoc)) {
        return validateNIE(cleanDoc);
      } else if (/^[A-Z]\d{7}[A-Z0-9]$/i.test(cleanDoc)) {
        return validateCIF(cleanDoc);
      }
      return { valid: true, document: cleanDoc };
  }
};

/**
 * Valida un nombre de persona o empresa
 * - Mínimo 2 palabras o 1 palabra con +3 caracteres
 * - No permite valores genéricos
 */
export const validateName = (name, options = {}) => {
  const { required = true, minWords = 1, minChars = 3 } = options;
  
  let cleanName = name?.toString().trim() || '';
  
  // Si está vacío y no es requerido, OK
  if (!cleanName && !required) {
    return { valid: true, name: '' };
  }
  
  // Si está vacío y es requerido
  if (!cleanName) {
    return {
      valid: false,
      error: 'El nombre es obligatorio',
      name: ''
    };
  }
  
  // Verificar longitud mínima
  if (cleanName.length < minChars) {
    return {
      valid: false,
      error: `El nombre debe tener al menos ${minChars} caracteres`,
      name: cleanName
    };
  }
  
  // Verificar valores genéricos
  const lowerName = cleanName.toLowerCase();
  if (BLOCKED_GENERIC_VALUES.includes(lowerName)) {
    return {
      valid: false,
      error: 'Introduce un nombre real',
      name: cleanName
    };
  }
  
  // Verificar número de palabras si se requiere más de una
  if (minWords > 1) {
    const words = cleanName.split(/\s+/).filter(w => w.length > 0);
    if (words.length < minWords) {
      return {
        valid: false,
        error: `Introduce nombre completo (mínimo ${minWords} palabras)`,
        name: cleanName
      };
    }
  }
  
  // Capitalizar automáticamente
  cleanName = cleanName
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  return {
    valid: true,
    name: cleanName,
    formatted: cleanName
  };
};

/**
 * Valida una contraseña
 * - Mínimo 8 caracteres
 * - Al menos una mayúscula, una minúscula y un número
 */
export const validatePassword = (password, options = {}) => {
  const { required = true, minLength = 8 } = options;
  
  const pwd = password || '';
  
  if (!pwd && !required) {
    return { valid: true };
  }
  
  if (!pwd) {
    return {
      valid: false,
      error: 'La contraseña es obligatoria'
    };
  }
  
  if (pwd.length < minLength) {
    return {
      valid: false,
      error: `La contraseña debe tener al menos ${minLength} caracteres`
    };
  }
  
  if (!/[A-Z]/.test(pwd)) {
    return {
      valid: false,
      error: 'La contraseña debe incluir al menos una mayúscula'
    };
  }
  
  if (!/[a-z]/.test(pwd)) {
    return {
      valid: false,
      error: 'La contraseña debe incluir al menos una minúscula'
    };
  }
  
  if (!/[0-9]/.test(pwd)) {
    return {
      valid: false,
      error: 'La contraseña debe incluir al menos un número'
    };
  }
  
  return { valid: true };
};

// ==================== UTILIDADES ====================

/**
 * Formatea un teléfono para mostrar
 */
export const formatPhone = (phone) => {
  const clean = phone?.toString().replace(/\D/g, '') || '';
  if (clean.length !== 9) return phone;
  return `${clean.slice(0,3)} ${clean.slice(3,6)} ${clean.slice(6)}`;
};

/**
 * Detecta el tipo de documento automáticamente
 */
export const detectDocumentType = (doc) => {
  const clean = doc?.toString().trim().toUpperCase() || '';
  
  if (/^\d{8}[A-Z]$/.test(clean)) return 'DNI';
  if (/^[XYZ]\d{7}[A-Z]$/.test(clean)) return 'NIE';
  if (/^[A-Z]\d{7}[A-Z0-9]$/.test(clean)) return 'CIF';
  return 'OTRO';
};

/**
 * Valida un formulario completo
 */
export const validateForm = (data, rules) => {
  const errors = {};
  let isValid = true;
  
  for (const [field, rule] of Object.entries(rules)) {
    const value = data[field];
    let result;
    
    switch (rule.type) {
      case 'phone':
        result = validatePhone(value, rule.options);
        break;
      case 'email':
        result = validateEmail(value, rule.options);
        break;
      case 'address':
        result = validateAddress(value, rule.options);
        break;
      case 'name':
        result = validateName(value, rule.options);
        break;
      case 'document':
        result = validateDocument(value, rule.documentType, rule.options);
        break;
      case 'password':
        result = validatePassword(value, rule.options);
        break;
      default:
        result = { valid: true };
    }
    
    if (!result.valid) {
      errors[field] = result.error;
      isValid = false;
    }
  }
  
  return { isValid, errors };
};

export default {
  validatePhone,
  validateEmail,
  validateAddress,
  validateName,
  validateDocument,
  validateDNI,
  validateNIE,
  validateCIF,
  validatePassword,
  formatPhone,
  detectDocumentType,
  validateForm,
  EU_COUNTRIES
};
